import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { FaWhatsapp } from 'react-icons/fa6'
import { LuX, LuSend, LuClock, LuSparkles } from 'react-icons/lu'
import logoIcon from '#/assets/logo-icon-yellow.png'

const WHATSAPP_NUMBER = '201550660071'
const DEFAULT_GREETING =
  'Assalamu Alaikum! I would like to inquire about Quran & Islamic courses at Waallamah.'

const QUICK_PROMPTS = [
  {
    icon: '📖',
    label: 'Quran & Tajweed Courses',
    text: 'Assalamu Alaikum! I would like more details about Quran & Tajweed courses.',
  },
  {
    icon: '🎁',
    label: 'Book Free Trial Session',
    text: 'Assalamu Alaikum! I would like to book a free trial session with an instructor.',
  },
  {
    icon: '💳',
    label: 'Plans & Pricing Info',
    text: 'Assalamu Alaikum! I have questions regarding your monthly plans and pricing.',
  },
  {
    icon: '👶',
    label: 'Kids Learning Program',
    text: 'Assalamu Alaikum! I want to inquire about Quran lessons suitable for children.',
  },
]

export default function FloatingWhatsApp() {
  const [isOpen, setIsOpen] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const [inputMessage, setInputMessage] = useState('')

  // Show friendly bubble after 2.5 seconds initially
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowTooltip(true)
    }, 2500)
    return () => clearTimeout(timer)
  }, [])

  const handleOpenWhatsApp = (textToSend?: string) => {
    const msg = (textToSend ?? inputMessage).trim() || DEFAULT_GREETING
    const encoded = encodeURIComponent(msg)
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encoded}`
    window.open(url, '_blank', 'noopener,noreferrer')
    setInputMessage('')
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleOpenWhatsApp()
    }
  }

  return (
    <div className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-50 flex flex-col items-end pointer-events-none">
      {/* Floating Chat Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{
              opacity: 0,
              scale: 0.88,
              y: 20,
              transformOrigin: 'bottom right',
            }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.88, y: 15 }}
            transition={{ type: 'spring', damping: 25, stiffness: 350 }}
            className="pointer-events-auto mb-3 w-[calc(100vw-2.5rem)] sm:w-[380px] max-w-[400px] overflow-hidden rounded-3xl border border-emerald-500/20 bg-white/95 dark:bg-slate-900/95 shadow-2xl backdrop-blur-xl transition-all"
            role="dialog"
            aria-label="WhatsApp Support Chat"
          >
            {/* Header */}
            <div className="relative overflow-hidden bg-gradient-to-r from-[#075E54] via-[#128C7E] to-[#0f766e] px-4.5 py-4 text-white">
              {/* Subtle islamic star / shimmer background overlay */}
              <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-emerald-300/10 blur-2xl pointer-events-none" />
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-white/10 via-transparent to-transparent pointer-events-none" />

              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15 p-1.5 ring-2 ring-white/30 backdrop-blur-md">
                      <img
                        src={logoIcon}
                        alt="Waallamah Support"
                        className="h-full w-full object-contain"
                      />
                    </div>
                    {/* Live Online Badge */}
                    <span className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                      <span className="relative inline-flex h-2.5 w-2.5 rounded-full border-2 border-[#075E54] bg-[#25D366]" />
                    </span>
                  </div>

                  <div>
                    <div className="flex items-center gap-1.5">
                      <h3 className="text-base font-semibold leading-tight tracking-tight text-white">
                        Waallamah Support
                      </h3>
                      <span className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-medium text-emerald-100 backdrop-blur-xs">
                        <LuSparkles className="h-2.5 w-2.5 text-yellow-300" />
                        Online
                      </span>
                    </div>
                    <p className="flex items-center gap-1 text-xs text-emerald-100/80">
                      <LuClock className="h-3 w-3" />
                      Typically replies in under 5 mins
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="rounded-full p-1.5 text-white/80 transition-colors hover:bg-white/15 hover:text-white focus:outline-none"
                  aria-label="Close chat"
                >
                  <LuX className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Chat Body */}
            <div className="relative max-h-[360px] overflow-y-auto p-4 space-y-3.5 bg-neutral-50/60 dark:bg-slate-950/40">
              {/* Automated Greeting Bubble */}
              <div className="flex gap-2.5 items-start">
                <div className="flex-1 rounded-2xl rounded-tl-sm bg-white dark:bg-slate-800 p-3.5 shadow-sm border border-slate-200/60 dark:border-slate-700/60">
                  <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400 mb-1">
                    Waallamah Student Advisor
                  </p>
                  <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
                    Assalamu Alaikum wa Rahmatullah! 🌙
                    <br />
                    How can we help you today with your Quran recitation,
                    Tajweed, or Arabic studies?
                  </p>
                  <span className="mt-1.5 block text-right text-[10px] text-slate-400">
                    Just now
                  </span>
                </div>
              </div>

              {/* Quick Action Pills */}
              <div className="space-y-1.5 pt-1">
                <p className="text-[11px] font-medium tracking-wide uppercase text-slate-500 dark:text-slate-400 px-1">
                  Suggested topics
                </p>
                <div className="grid grid-cols-1 gap-1.5">
                  {QUICK_PROMPTS.map((prompt) => (
                    <button
                      key={prompt.label}
                      type="button"
                      onClick={() => handleOpenWhatsApp(prompt.text)}
                      className="group flex items-center justify-between rounded-xl border border-emerald-500/15 bg-white/90 dark:bg-slate-800/80 px-3 py-2 text-left text-xs font-medium text-slate-700 dark:text-slate-200 shadow-xs transition-all duration-150 hover:border-emerald-500 hover:bg-emerald-50/70 dark:hover:bg-emerald-950/30 hover:text-emerald-700 dark:hover:text-emerald-300"
                    >
                      <span className="flex items-center gap-2">
                        <span className="text-sm">{prompt.icon}</span>
                        <span>{prompt.label}</span>
                      </span>
                      <FaWhatsapp className="h-3.5 w-3.5 text-emerald-500 opacity-60 transition-opacity group-hover:opacity-100" />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Input & Direct Send Footer */}
            <div className="border-t border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 p-3">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your message..."
                  className="flex-1 rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 px-4 py-2.5 text-xs text-slate-800 dark:text-slate-100 placeholder-slate-400 focus:border-emerald-500 focus:bg-white dark:focus:bg-slate-800 focus:outline-none transition-all"
                />
                <button
                  type="button"
                  onClick={() => handleOpenWhatsApp()}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-tr from-[#25D366] to-[#128C7E] text-white shadow-md transition-all hover:scale-105 active:scale-95 focus:outline-none"
                  aria-label="Send WhatsApp message"
                >
                  <LuSend className="h-4 w-4 ml-0.5" />
                </button>
              </div>
              <div className="mt-2 flex items-center justify-center gap-1.5 text-[10px] text-slate-400 dark:text-slate-500">
                <FaWhatsapp className="h-3 w-3 text-emerald-500" />
                <span>Direct WhatsApp connection (+20 155 066 0071)</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Action Trigger Button & Tooltip */}
      <div className="pointer-events-auto flex items-center gap-3">
        {/* Tooltip speech pill */}
        <AnimatePresence>
          {showTooltip && !isOpen && (
            <motion.div
              initial={{ opacity: 0, x: 15, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 10, scale: 0.9 }}
              transition={{ duration: 0.2 }}
              onClick={() => {
                setIsOpen(true)
                setShowTooltip(false)
              }}
              className="group hidden sm:flex cursor-pointer items-center gap-2 rounded-full border border-emerald-500/20 bg-white/95 dark:bg-slate-900/95 py-2 pl-3.5 pr-2.5 text-xs font-semibold text-slate-800 dark:text-slate-100 shadow-xl backdrop-blur-md transition-all hover:border-emerald-500 hover:shadow-emerald-500/10"
            >
              <span className="flex h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
              <span>Need help? Chat with us</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  setShowTooltip(false)
                }}
                className="ml-1 rounded-full p-0.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                aria-label="Dismiss tooltip"
              >
                <LuX className="h-3.5 w-3.5" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Floating Button */}
        <div className="relative group">
          {/* Subtle animated halo glow */}
          <span className="absolute -inset-1.5 rounded-full bg-gradient-to-r from-emerald-400 to-[#25D366] opacity-35 blur-md transition-all duration-500 group-hover:opacity-75 group-hover:blur-lg animate-pulse" />

          <motion.button
            type="button"
            onClick={() => {
              setIsOpen((prev) => !prev)
              setShowTooltip(false)
            }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.92 }}
            className="relative flex h-14 w-14 sm:h-15 sm:w-15 items-center justify-center rounded-full bg-gradient-to-tr from-[#128C7E] via-[#25D366] to-[#25D366] text-white shadow-xl shadow-emerald-600/30 transition-shadow focus:outline-none focus:ring-4 focus:ring-emerald-400/40"
            aria-label={
              isOpen
                ? 'Close WhatsApp chat'
                : 'Open WhatsApp chat (+20 155 066 0071)'
            }
          >
            <AnimatePresence mode="wait">
              {isOpen ? (
                <motion.div
                  key="close-icon"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <LuX className="h-7 w-7" />
                </motion.div>
              ) : (
                <motion.div
                  key="whatsapp-icon"
                  initial={{ scale: 0.7, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.7, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <FaWhatsapp className="h-8 w-8 drop-shadow-sm" />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Green active dot badge */}
            {!isOpen && (
              <span className="absolute right-0 top-0 flex h-4 w-4 items-center justify-center">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
                <span className="relative inline-flex h-3 w-3 rounded-full border-2 border-white dark:border-slate-900 bg-emerald-400" />
              </span>
            )}
          </motion.button>
        </div>
      </div>
    </div>
  )
}
